import { useMemo } from 'react'
import { homerunnerApiBaseUrl } from '../../hr'
import { useQuery } from '@tanstack/react-query'
import { PropertyMapFilter } from './config'
import { usePropertyDetails, type PropertyDetailsRaw } from '../../common/property-details'

type PropertyImage = {
  id: number
  url: string
  position: number
  caption: string | null
}

export type PropertyMapData = {
  id: string
  title: string
  slug: string
  price: number
  currency?: string
  propertyType: string
  images: string[]
  location: {
    label: string
    lat: number
    lng: number
  }
  features: {
    guests: number
    beds: number
    baths: number
  }
}

function toPropertyMapData(raw: PropertyDetailsRaw, images: PropertyImage[], slug: string): PropertyMapData {
  return {
    id: raw.uuid ?? String(raw.id),
    title: raw.name ?? raw.title,
    slug,
    price: Number(raw.base_daily_rate) || 0,
    currency: raw.currency_code || undefined,
    propertyType: raw.type?.name ?? '',
    images: images.map(img => img.url),
    location: {
      label: raw.city?.name ?? '',
      lat: Number(raw.latitude) || 0,
      lng: Number(raw.longitude) || 0,
    },
    features: {
      guests: Number(raw.maximum_guest_count) || 0,
      beds: Number(raw.bedroom_count) || 0,
      baths: Number(raw.bathroom_count) || 0,
    },
  }
}

export function propertyMapImageKey(filter: PropertyMapFilter) {
  return ['property-map-image', filter.feed_id, filter.property, filter.platform] as const
}

export async function fetchPropertyImages(filter: PropertyMapFilter): Promise<PropertyImage[]> {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-images`)
  if (filter.feed_id) url.searchParams.append('feed_id', String(filter.feed_id))
  if (filter.property) url.searchParams.append('property', filter.property)
  if (filter.platform) url.searchParams.append('platform', filter.platform)
  url.searchParams.append('limit', '1') // only need first image for tooltip

  const res = await fetch(url.toString())
  if (!res.ok) return []
  const data = await res.json() as { status: string; result: PropertyImage[] }
  return data.result ?? []
}

/**
 * The map needs the resolved property details (shared with property-title /
 * property-meta via the ['property-details', filter] query — one fetch per page)
 * plus a single thumbnail image. Details and image are separate queries so the
 * details half dedupes with the sibling widgets; combined here into PropertyMapData.
 */
export function useFetchPropertyForMap(filter: PropertyMapFilter) {
  const enabled = !!filter.feed_id && !!filter.property
  const detailsQuery = usePropertyDetails(filter, { enabled })
  const imagesQuery = useQuery({
    queryKey: propertyMapImageKey(filter),
    queryFn: () => fetchPropertyImages(filter),
    enabled,
  })

  const data = useMemo(() => {
    if (!detailsQuery.data) return undefined
    return toPropertyMapData(detailsQuery.data, imagesQuery.data ?? [], filter.property)
  }, [detailsQuery.data, imagesQuery.data, filter.property])

  return {
    data,
    isLoading: detailsQuery.isLoading || imagesQuery.isLoading,
    isError: detailsQuery.isError,
    error: detailsQuery.error,
  }
}
